/**
 * Platform param GROUPS — a whole `z.object` the runtime ships (see ./files-options.ts), not a control.
 *
 * They travel in the same `uiComponent.type` slot as a control token, so they belong in the map below. They
 * are named apart from it because **nothing renders a single input for one**: a consumer reads the token to
 * decide how to draw the whole group, and a registry of one-input-per-token has no entry for it.
 *
 * An author's own `z.object` carries none of these.
 */
export const FlowExtensionParamGroupTypes = {
  FILE_UPLOAD_OPTIONS: 'FILE_UPLOAD_OPTIONS',
  FILE_LIST_OPTIONS  : 'FILE_LIST_OPTIONS',
  FILE_GET_OPTIONS   : 'FILE_GET_OPTIONS',
  FILE_DELETE_OPTIONS: 'FILE_DELETE_OPTIONS',
}

export const FlowExtensionParamWidgetTypes = {
  // Derived from a zod declaration — see `deriveWidget`.
  SINGLE_LINE_TEXT: 'SINGLE_LINE_TEXT',
  MULTI_LINE_TEXT : 'MULTI_LINE_TEXT',
  NUMERIC_STEPPER : 'NUMERIC_STEPPER',
  DATE_PICKER     : 'DATE_PICKER',
  TOGGLE          : 'TOGGLE',
  DROPDOWN        : 'DROPDOWN',

  ...FlowExtensionParamGroupTypes,

  // legacy — no zod declaration produces these. They reach the console only from a legacy JSDoc-format
  // service, which writes `uiComponent` by hand. Kept because the console still renders them for an
  // already-installed extension.
  NUMERIC         : 'NUMERIC',
  CHECKBOX        : 'CHECKBOX',
  DATE_TIME_PICKER: 'DATE_TIME_PICKER',
  TIME_PICKER     : 'TIME_PICKER',
}

/**
 * The widget tokens a declaration carries EXPLICITLY, as author metadata (`meta.frWidget`) — everything else
 * in the map above is DERIVED from the zod type by `deriveWidget`.
 *
 * `MULTI_LINE_TEXT` is `z.text()` / `z.json()`; the `FILE_*_OPTIONS` tokens name a platform param group.
 */
export type FrWidgetToken =
  | 'MULTI_LINE_TEXT'
  | 'FILE_UPLOAD_OPTIONS'
  | 'FILE_LIST_OPTIONS'
  | 'FILE_GET_OPTIONS'
  | 'FILE_DELETE_OPTIONS'

/** The subset that names a platform param GROUP rather than a single control. */
export type FrGroupToken = Exclude<FrWidgetToken, 'MULTI_LINE_TEXT'>
